'use client'

import dynamic from 'next/dynamic'
import { C, Exercise, Figure, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/numbers'

const PowerExplorer = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.PowerExplorer), { ssr: false })
const CompoundE = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.CompoundE), { ssr: false })
const LogStairs = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.LogStairs), { ssr: false })

export default function ExponentsAndLogs() {
  return (
    <Lesson>
      <Step gate="nq-pow">
        <H>Repeated multiplication</H>
        <P>
          Multiplication is repeated adding: <M>{'4 \\times 3 = 4 + 4 + 4'}</M>. A <strong>power</strong> (or exponent) is
          repeated multiplying: <M>{'2^5 = 2 \\times 2 \\times 2 \\times 2 \\times 2 = 32'}</M>. The small raised number says how
          many copies to multiply.
        </P>
        <Figure n="2.7" caption="Drag the exponent. Whole numbers show the copies being multiplied; in between, the curve fills in smoothly. Try negative exponents too.">
          <PowerExplorer />
        </Figure>
        <P>Three rules fall out of the picture:</P>
        <ul className="gd-list">
          <li>
            <M>{'2^a \\times 2^b = 2^{a+b}'}</M> — multiplying powers adds the counts (3 twos times 2 twos is 5 twos).
          </li>
          <li>
            <M>{'2^0 = 1'}</M> — multiplying zero copies leaves you where you started: 1.
          </li>
          <li>
            <M>{'2^{-1} = \\tfrac{1}{2}'}</M> — a negative exponent means divide instead of multiply.
          </li>
        </ul>
        <NumberQuiz id="nq-pow" question={<>What is <M>{'3^4'}</M>?</>} answer={81} explain={<>3 × 3 × 3 × 3 = 9 × 9 = 81.</>} />
      </Step>

      <Step gate="q-e">
        <H>The special number e</H>
        <P>
          Some growth happens <em>continuously</em> — populations, interest, a virus. When you grow by 100% but in smaller and
          smaller instalments, something remarkable happens: the result doesn’t explode. It settles on a specific number.
        </P>
        <Figure n="2.8" caption="Growing by 100% in more and more pieces. The result approaches e ≈ 2.71828.">
          <CompoundE />
        </Figure>
        <P>
          That number is <M>{'e \\approx 2.71828'}</M>, and <M>{'e^x'}</M> is called <strong>the exponential function</strong>. It
          has a magic property you’ll appreciate in Module 4: the steepness of the curve <M>{'e^x'}</M> at any point equals its
          height. It shows up all over machine learning — in softmax, in sigmoid, in probabilities.
        </P>
        <Quiz
          id="q-e"
          question="Roughly how big is e?"
          options={[{ label: 'About 1.41', why: 'That’s √2.' }, { label: 'About 2.718', correct: true }, { label: 'About 3.14', why: 'That’s π, a different famous number.' }, { label: 'It grows forever', why: 'That’s the surprise: splitting growth into ever smaller pieces converges.' }]}
        />
      </Step>

      <Step gate="nq-log">
        <H>Logarithms: “how many times?”</H>
        <P>
          A <strong>logarithm</strong> runs a power backwards. Instead of “what’s 2 multiplied 5 times?” (32) it asks: “how many
          times must I multiply by 2 to reach 32?” (5).
        </P>
        <MB>{'2^{\\green{5}} = 32 \\quad\\Longleftrightarrow\\quad \\log_2(32) = \\green{5}'}</MB>
        <Figure n="2.9" caption="log₂(N) counts the doublings from 1 to N. Notice how slowly it grows: going from 32 to 64 only adds one.">
          <LogStairs />
        </Figure>
        <P>
          The base can be anything. <M>{'\\log_{10}(1000) = 3'}</M> because <M>{'10^3 = 1000'}</M>. The most common in machine
          learning is base e, called the <strong>natural log</strong> and written <M>{'\\ln'}</M> — in code, <C>Math.log</C>.
        </P>
        <NumberQuiz id="nq-log" question={<>What is <M>{'\\log_2(64)'}</M>?</>} answer={6} explain={<>2 × 2 × 2 × 2 × 2 × 2 = 64: six doublings.</>} />
      </Step>

      <Step gate="q-logrule">
        <H>Why AI loves logarithms</H>
        <P>Logs turn multiplication into addition:</P>
        <MB>{'\\log(a \\times b) = \\log(a) + \\log(b)'}</MB>
        <P>
          This matters enormously. A language model computes the probability of a sentence by multiplying the probabilities of
          every word — say 0.1 × 0.05 × 0.2 × … for hundreds of words. That product becomes so tiny a computer rounds it to zero.
          Take logs, and it becomes a harmless sum. That’s why you’ll see “log-probabilities” everywhere.
        </P>
        <Quiz
          id="q-logrule"
          question={<>Using the rule, what is <M>{'\\log_2(8) + \\log_2(4)'}</M>?</>}
          options={[{ label: '5', correct: true }, { label: '12', why: 'Logs of a product add — but here we add the logs themselves: 3 + 2.' }, { label: '32', why: 'That’s 8 × 4 — the log of it is what we want: log₂(32).' }, { label: '6', why: 'log₂(8) is 3, log₂(4) is 2.' }]}
          explain="3 + 2 = 5, which is log₂(32) — and 32 = 8 × 4. The rule works."
        />
      </Step>

      <Step gate="logs">
        <H>In code</H>
        <Exercise id="powers" title="Powers and logs in TypeScript" {...EX.powers} />
        <Exercise
          id="logs"
          title="Count doublings, change bases"
          {...EX.logs}
          hints={[
            <>
              A <C>while</C> loop repeats as long as a condition holds: <C>{'while (value < n) { value *= 2; count++ }'}</C>.
            </>,
            <>
              <C>return Math.log(x) / Math.log(base)</C>
            </>,
          ]}
        />
      </Step>
    </Lesson>
  )
}
